import { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";

interface BackToTopButtonProps {
  scrollThreshold?: number; // pixels to scroll before showing
}

export const BackToTopButton = ({ scrollThreshold = 400 }: BackToTopButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const { light } = useHapticFeedback();

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling approximately 2 screen heights (or custom threshold)
      setIsVisible(window.scrollY > scrollThreshold);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrollThreshold]);

  const scrollToTop = () => {
    light(); // Haptic feedback on tap
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-20 right-4 z-40"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <Button
            onClick={scrollToTop}
            size="icon"
            className={cn(
              "h-12 w-12 rounded-full shadow-lg",
              "bg-primary/90 hover:bg-primary text-primary-foreground",
              "backdrop-blur-sm"
            )}
            aria-label="Scroll to top"
          >
            <ChevronUp className="w-5 h-5" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Utility function to scroll to top - can be used by other components
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};
